import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Radio, RadioGroup } from "@nextui-org/react";
import { ZButton } from "@/shared/components/button";
import { InfoIcon } from "lucide-react";
import { useState } from "react";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { ZCard, ZCardBody } from "@/shared/components/card";

interface NistCsfReassessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed?: (option: string) => void;
  loading?: boolean;
}

const assessmentOptions = [
  {
    id: "assess_1",
    value: "REASSESS_ALL",
    label: "Reassess all controls",
    tooltipText: "This action will override all previous responses",
  },
  {
    id: "assess_2",
    value: "RETAIN_ACCEPTED",
    label: "Reassess all controls except those that have been accepted",
    tooltipText: "Responses already accepted will not be reassessed. Please keep them accurate and relevant to the new files",
  },
  {
    id: "assess_3",
    value: "RETAIN_EDITED_ACCEPTED",
    label: "Reassess all controls except those that have been accepted or edited",
    tooltipText: "Responses already accepted or edited will not be reassessed. Please keep them accurate and relevant to the new files",
  },
];

const NistCsfReassessModal = ({ isOpen, onClose, onProceed, loading }: NistCsfReassessModalProps) => {
  const [selectedOption, setSelectedOption] = useState<string>(assessmentOptions[0].value);

  const onReassessOptionClick = (option: string) => {
    setSelectedOption(option);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" classNames={{base: "w-[604px]"}}>
      <ModalContent>
        <ModalHeader className="flex flex-col items-center pb-2">
          <span className="text-xl font-semibold">Configure Reassessment</span>
        </ModalHeader>
        <ModalBody>
          <div className="text-center text-gray-600 text-sm mb-4">
            Controls you've updated can be reassessed. Select the desired controls below.
          </div>
          <RadioGroup
            label=""
            className="pt-4"
            value={selectedOption}
            onChange={e => onReassessOptionClick(e.target.value)}
          >
            {assessmentOptions.map((option) => (
              <ZCard
                key={option.id}
                shadow="none"
                radius="md"
                className="border border-solid"
              >
                <ZCardBody>
                  <Radio value={option.value}>
                    <div className="radio-container pl-2">
                      <span className="label text-[14px] font-medium text-zinc-900">
                        {option.label}
                        <ZTooltip
                          size="lg"
                          content={option.tooltipText}
                          placement="right"
                          delay={0}
                          isDisabled={false}
                          showArrow={true}
                          className="max-w-[310px]"
                        >
                          <InfoIcon className="pl-2 inline" />
                        </ZTooltip>
                      </span>
                    </div>
                  </Radio>
                </ZCardBody>
              </ZCard>
            ))}
          </RadioGroup>
        </ModalBody>
        <ModalFooter>
          <ZButton
            color="default"
            variant="bordered"
            className="min-w-[120px]"
            onClick={onClose}
          >
            Discard
          </ZButton>
          <ZButton
            color="primary"
            className="min-w-[120px]"
            onClick={() => onProceed?.(selectedOption)}
            isLoading={loading}
          >
            Proceed
          </ZButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NistCsfReassessModal; 